<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="text-h6 d-flex align-center">
        <v-icon class="mr-2">mdi-test-tube</v-icon>
        功能性测试
      </v-card-title>
      <v-divider />

      <v-card-text class="pt-4">
        <v-alert
          v-if="tenantSelectionRequired"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-4"
        >
          超级管理员请先在右侧“操作记录”区域选择要测试的租户。
        </v-alert>

        <v-card variant="outlined" class="mb-4">
          <v-card-title class="text-subtitle-1 d-flex align-center">
            <v-icon class="mr-2" color="warning">mdi-tune</v-icon>
            舵机可用性校验
          </v-card-title>
          <v-card-text class="pt-0 text-body-2 text-grey">
            对当前设备的舵机进行绝对角度往返测试，校验舵机是否可以正常运行。
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn
              color="warning"
              variant="outlined"
              prepend-icon="mdi-play-circle-outline"
              :disabled="batchRunning"
              @click="$emit('open-servo-check')"
            >
              开始校验
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-card variant="outlined">
          <v-card-title class="text-subtitle-1 d-flex align-center">
            <v-icon class="mr-2" color="deep-purple">mdi-devices</v-icon>
            批量测试设备
          </v-card-title>
          <v-card-text class="pt-1">
            <div class="d-flex align-center mb-2">
              <span class="text-caption text-grey">
                设备来自当前租户的可调试设备列表
              </span>
              <v-spacer />
              <v-btn
                size="small"
                variant="text"
                :disabled="devices.length === 0 || batchRunning"
                @click="toggleAllDevices"
              >
                {{ allSelected ? '取消全选' : '全选' }}
              </v-btn>
            </div>

            <v-select
              :model-value="batchDeviceIds"
              :items="devices"
              item-title="name"
              item-value="id"
              label="选择测试设备"
              variant="outlined"
              density="compact"
              multiple
              chips
              closable-chips
              :loading="devicesLoading"
              :disabled="batchRunning"
              :no-data-text="tenantSelectionRequired
                ? '请先选择租户'
                : (devicesLoading ? '正在加载设备...' : '当前租户暂无可调试设备')"
              @update:model-value="$emit('update:batchDeviceIds', $event)"
            />

            <v-text-field
              :model-value="loopCount"
              label="循环次数"
              type="number"
              min="1"
              max="999"
              step="1"
              variant="outlined"
              density="compact"
              :disabled="batchRunning"
              @update:model-value="updateLoopCount"
            />

            <v-alert type="info" variant="tonal" density="compact">
              请先在右侧“操作记录”中勾选至少 2 条记录。点击下方按钮后，可继续编排组合表情的顺序和间隔；执行时每个选中设备都会同步运行，并按设置次数循环。
            </v-alert>
          </v-card-text>
          <v-card-actions class="px-4 pb-4">
            <v-spacer />
            <v-btn
              color="deep-purple"
              variant="flat"
              prepend-icon="mdi-playlist-play"
              :disabled="batchDeviceIds.length === 0 || batchRunning"
              @click="$emit('open-batch-combo')"
            >
              配置并运行组合表情
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-3">
        <v-spacer />
        <v-btn variant="outlined" @click="$emit('update:modelValue', false)">关闭</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  devices: { type: Array, default: () => [] },
  devicesLoading: { type: Boolean, default: false },
  batchDeviceIds: { type: Array, default: () => [] },
  loopCount: { type: Number, default: 1 },
  batchRunning: { type: Boolean, default: false },
  tenantSelectionRequired: { type: Boolean, default: false }
})

const emit = defineEmits([
  'update:modelValue',
  'update:batchDeviceIds',
  'update:loopCount',
  'open-servo-check',
  'open-batch-combo'
])

const allSelected = computed(() => (
  props.devices.length > 0
  && props.devices.every(device => props.batchDeviceIds.includes(device.id))
))

function toggleAllDevices() {
  emit(
    'update:batchDeviceIds',
    allSelected.value ? [] : props.devices.map(device => device.id)
  )
}

function updateLoopCount(value) {
  const parsed = Math.floor(Number(value))
  emit('update:loopCount', Number.isFinite(parsed) ? Math.min(999, Math.max(1, parsed)) : 1)
}
</script>
